/**
 * The typed resource-kind registry: one declaration per owner-scoped
 * resource *type* (like route postures), never per instance.
 *
 * `ownerId` used to carry three jobs at once — ownership access, an
 * inferred scoping rule, and creator lineage. The registry moves the
 * scope rule out of inference and into a declaration: each kind names
 * which owner scopes are allowed to fence its rows, and creation parses
 * the caller-supplied owner into a typed {@link OwnerRef} validated
 * against those scopes. There is deliberately **no default scope** — the
 * owning scope is named explicitly at creation (kinds with a single
 * eligible scope have it forced; kinds with several make the caller
 * choose) — and **no `'host'` scope**: `ownerId IS NULL` is never an
 * eligible owner for any kind. Existing NULL rows are surfaced by the
 * owner data audit (`server/src/lib/resource-owner-audit.ts`), not
 * declared legitimate.
 *
 * Declarations are contributed per plugin on
 * `PluginServerDefinition.resourceKinds` and boot-asserted over the
 * composed plugin set (`assertPluginResourceKinds` in
 * `server/src/lib/resource-kinds.ts`), mirroring `assertPluginMcpPostures`.
 *
 * See `docs/internal/architecture/resource-kinds.md`.
 */

import { CORE_IDS, type ResourceGrantAccess } from '@reflex/shared';
import type { OwnerScopeCaller } from './owner-scope.js';

declare const resourceKindNameBrand: unique symbol;

/**
 * Branded kind name (e.g. `'network-policy'`). Only
 * {@link defineResourceKind} mints one, so a free-form string cannot be
 * handed where a declared kind is expected.
 */
export type ResourceKindName = string & { readonly [resourceKindNameBrand]: 'ResourceKindName' };

/**
 * The host-native owner scopes. `'user'` / `'team'` / `'org'` map to the
 * `usr_` / `tem_` / `org_` id prefixes from `@reflex/shared`.
 */
export type CoreEligibleScope = 'user' | 'team' | 'org';

/**
 * A plugin-contributed owner scope (e.g. projects). The pair must match a
 * registered `OwnerKindContributor` — both fields — which the boot
 * assertion verifies, so a typo can't silently declare a scope nothing
 * resolves.
 */
export interface ContributedEligibleScope {
  /** Contributor kind, e.g. `'project'`. */
  readonly kind: string;
  /** Contributor id prefix, e.g. `'prj'`. */
  readonly idPrefix: string;
}

export type EligibleScope = CoreEligibleScope | ContributedEligibleScope;

/**
 * The kind's permission slugs, derived from its declared
 * {@link ResourceKindDeclaration.permissionDomain}. A kind names a domain
 * (plus, when `write` is not its mutation-tier verb, that verb), never a
 * pair of slugs, so the spelling is a property of the registry rather than
 * something each declaration gets right on its own.
 */
export interface ResourceKindPermissions {
  /** The read slug (e.g. `'personas:read'`). */
  readonly read: string;
  /**
   * The strict mutation-tier slug that unlocks the **org tier only**
   * (e.g. `'personas:write'`, or `'projects:manage'` for a domain whose
   * verb is `manage`) — it never grants writes to another user's or
   * team's rows. Consumed by `createOwnerScopedRouteAuthz` and the MCP
   * owner guard, which used to take it as a separate argument.
   */
  readonly write: string;
}

/**
 * The verbs a kind may name as its mutation tier. `write` is content
 * mutation; `manage` is administration of the domain itself. Both are
 * standard vocabulary — a domain uses the one that names what its tier
 * actually grants.
 */
export type ResourceKindWriteAction = 'write' | 'manage';

/**
 * Where the kind's rows live, so the host can read a row's owner without
 * knowing anything else about the kind. Two things key off it: the owner
 * data audit (which groups a table's owners by scope) and grant
 * management (which asks "who owns this id, and does it exist?"). Rows
 * are assumed keyed by `id` and scoped by `organization_id`, the
 * convention every owner-scoped table follows.
 *
 * Declaring it is what makes a kind shareable from the resource side: a
 * kind without it is admin-managed, because nobody can tell whose row it
 * is.
 */
export interface ResourceKindOwnerStorage {
  /** SQL table name, e.g. `'agent_personas'`. */
  readonly table: string;
  /** Owner column name; defaults to `'owner_id'`. */
  readonly ownerColumn?: string;
}

export interface ResourceKindDeclaration {
  readonly kind: ResourceKindName;
  /**
   * The owner scopes a row of this kind may be fenced by. Non-empty and
   * explicit — a kind with exactly one entry has that scope forced at
   * creation; a kind with several requires the caller to choose.
   */
  readonly eligibleScopes: readonly [EligibleScope, ...EligibleScope[]];
  /** The permission domain the slugs below were derived from, e.g. `'personas'`. */
  readonly permissionDomain: string;
  readonly permissions: ResourceKindPermissions;
  readonly ownerStorage?: ResourceKindOwnerStorage;
}

/**
 * The slug a share tier demands on a row of this kind: `view` resolves to
 * the read slug, `edit` to the write slug.
 *
 * The two tiers are the ones a grant can express
 * (`RESOURCE_GRANT_ACCESSES`), not a claim about permission spelling:
 * the slug in each slot is whatever the kind declared, so
 * `projects:manage` sits in the write slot exactly as `flows:write` does.
 * A kind whose surface has more verbs than these two declares them on its
 * own `OwnedResourcePermissions` map (see `owned-resource.ts`), which is
 * open-ended; sharing only ever asks which of the two tiers applies.
 */
export function resourceKindGrantSlug(
  declaration: ResourceKindDeclaration,
  access: ResourceGrantAccess,
): string {
  return access === 'edit' ? declaration.permissions.write : declaration.permissions.read;
}

/**
 * Every slug the sharing role for `access` binds — what a share of this
 * kind at that tier actually confers on the one shared row.
 *
 * `edit` includes the read slug as well as the write one: sharing a
 * resource for editing without the right to open it would be a share
 * nobody can use, and the write-implies-read contract
 * (`deriveImpliedReadSlug`) says the same thing everywhere else.
 * {@link resourceKindGrantSlug} stays the single slug a *check* demands;
 * this is the set the role is bound to.
 */
export function resourceKindGrantSlugs(
  declaration: ResourceKindDeclaration,
  access: ResourceGrantAccess,
): string[] {
  const slugs =
    access === 'edit'
      ? [resourceKindGrantSlug(declaration, 'view'), declaration.permissions.write]
      : [resourceKindGrantSlug(declaration, 'view')];
  return [...new Set(slugs)];
}

const KIND_NAME_PATTERN = /^[a-z][a-z0-9]*(-[a-z0-9]+)*$/;
/** Same spelling as a kind name: permission domains are kebab too. */
const PERMISSION_DOMAIN_PATTERN = KIND_NAME_PATTERN;
const SQL_IDENTIFIER_PATTERN = /^[a-z][a-z0-9_]*$/;

const CORE_SCOPE_PREFIXES: Readonly<Record<CoreEligibleScope, string>> = {
  user: CORE_IDS.user,
  team: CORE_IDS.team,
  org: CORE_IDS.organization,
};

function isCoreScope(scope: EligibleScope): scope is CoreEligibleScope {
  return typeof scope === 'string';
}

/** Human-readable scope name, for error messages and audit output. */
export function eligibleScopeName(scope: EligibleScope): string {
  return isCoreScope(scope) ? scope : scope.kind;
}

function describeEligibleScopes(declaration: ResourceKindDeclaration): string {
  return declaration.eligibleScopes.map(eligibleScopeName).join(', ');
}

/**
 * Declare a resource kind. Validates the declaration's local shape at
 * module load — cross-set rules (kind uniqueness, contributor existence,
 * slug catalog membership) are the boot assertion's job.
 */
export interface ResourceKindInput {
  kind: string;
  eligibleScopes: readonly [EligibleScope, ...EligibleScope[]];
  /**
   * The permission domain guarding this kind, e.g. `'personas'`. The
   * `<domain>:read` and mutation-tier slugs come from it — a kind does
   * not spell its own slugs, so the domain↔kind mapping lives in one
   * place.
   *
   * Optional only for the kinds that already hold their slugs as a shared
   * constant ({@link permissions}); the domain is then read off the write
   * slug, so the registry still knows it.
   */
  permissionDomain?: string;
  /**
   * The domain's mutation-tier verb. Defaults to `'write'`; domains whose
   * tier is administration rather than content mutation (`projects:manage`,
   * `admin:manage`) say so. The verb is real vocabulary — declaring it here
   * keeps the slug derived without flattening `manage` into `write`.
   */
  writeAction?: ResourceKindWriteAction;
  /**
   * Explicit slugs, for a kind whose permission constants are the source
   * of truth elsewhere — the host's `agent` kind reuses
   * `AGENT_RESOURCE_PERMISSIONS`, which every agent surface already gates
   * on. Prefer {@link permissionDomain}: spelling slugs by hand is exactly
   * what the domain derivation exists to avoid.
   */
  permissions?: { read?: string; write: string };
  ownerStorage?: ResourceKindOwnerStorage;
}

export function defineResourceKind(declaration: ResourceKindInput): ResourceKindDeclaration {
  const { kind, eligibleScopes, writeAction = 'write', ownerStorage } = declaration;
  if (!declaration.permissionDomain && !declaration.permissions) {
    throw new TypeError(
      `Resource kind '${kind}' must declare a permissionDomain (or explicit permissions).`,
    );
  }
  const writeSlug =
    declaration.permissions?.write ?? `${declaration.permissionDomain}:${writeAction}`;
  // The domain is the write slug's own prefix when it was not declared, so
  // a kind that names its slugs still lands in the same vocabulary.
  const permissionDomain = declaration.permissionDomain ?? writeSlug.split(':')[0];
  // Always a distinct read slug. A kind whose read slot fell back to the
  // write slug would bind that slug to its `view` sharing role — a "can
  // view" share conferring writes.
  const readSlug = declaration.permissions?.read ?? `${permissionDomain}:read`;
  if (!KIND_NAME_PATTERN.test(kind)) {
    throw new TypeError(
      `Resource kind '${kind}' must be kebab-case (e.g. 'network-policy', 'agent-persona').`,
    );
  }
  if (eligibleScopes.length === 0) {
    throw new TypeError(`Resource kind '${kind}' declares no eligible owner scopes.`);
  }
  const seen = new Set<string>();
  for (const scope of eligibleScopes) {
    const name = eligibleScopeName(scope);
    if (seen.has(name)) {
      throw new TypeError(`Resource kind '${kind}' declares eligible scope '${name}' twice.`);
    }
    seen.add(name);
    if (!isCoreScope(scope) && !SQL_IDENTIFIER_PATTERN.test(scope.idPrefix)) {
      throw new TypeError(
        `Resource kind '${kind}': contributed scope '${scope.kind}' has a malformed id prefix '${scope.idPrefix}'.`,
      );
    }
  }
  if (!PERMISSION_DOMAIN_PATTERN.test(permissionDomain)) {
    throw new TypeError(
      `Resource kind '${kind}': permission domain '${permissionDomain}' must be lower-case ` +
        `kebab (e.g. 'network-policies'), matching the permission slug vocabulary.`,
    );
  }
  // Identifiers are interpolated into SQL by the audit and the owner
  // lookup, so they are validated here, once, at declaration time.
  for (const identifier of [ownerStorage?.table, ownerStorage?.ownerColumn]) {
    if (identifier !== undefined && !SQL_IDENTIFIER_PATTERN.test(identifier)) {
      throw new TypeError(
        `Resource kind '${kind}': ownerStorage identifier '${identifier}' is malformed.`,
      );
    }
  }
  return {
    kind: kind as ResourceKindName,
    eligibleScopes,
    permissionDomain,
    permissions: { read: readSlug, write: writeSlug },
    ownerStorage,
  };
}

/**
 * A parsed, scope-tagged owner reference. `ownerId` stops travelling as a
 * raw `string` at the creation boundary: parsing derives the scope from
 * the id's prefix and validates it against the kind's eligible scopes, so
 * an ineligible owner is a typed 400 rather than a convention violation
 * that persists silently.
 */
export type OwnerRef =
  | { readonly scope: CoreEligibleScope; readonly ownerId: string }
  | { readonly scope: 'contributed'; readonly kind: string; readonly ownerId: string };

/**
 * A rejected owner at the creation/transfer boundary: missing where the
 * kind requires a choice, the retired host-tier `null`, or a scope the
 * kind does not declare eligible. Client-fixable, hence 400 — unlike
 * `OwnerAuthorizationError` (403), which means the caller isn't a member
 * of an otherwise-valid owner.
 */
export class ResourceOwnerEligibilityError extends Error {
  readonly statusCode = 400;

  constructor(message: string) {
    super(message);
    this.name = 'ResourceOwnerEligibilityError';
  }
}

/**
 * Parse a concrete owner id against a kind declaration.
 *
 * Throws {@link ResourceOwnerEligibilityError} when the id's prefix maps
 * to no eligible scope for the kind (including unknown prefixes).
 */
export function parseOwnerRef(ownerId: string, declaration: ResourceKindDeclaration): OwnerRef {
  const separator = ownerId.indexOf('_');
  const prefix = separator > 0 ? ownerId.slice(0, separator) : undefined;
  if (prefix) {
    for (const scope of declaration.eligibleScopes) {
      if (isCoreScope(scope)) {
        if (CORE_SCOPE_PREFIXES[scope] === prefix) return { scope, ownerId };
      } else if (scope.idPrefix === prefix) {
        return { scope: 'contributed', kind: scope.kind, ownerId };
      }
    }
  }
  throw new ResourceOwnerEligibilityError(
    `'${ownerId}' is not an eligible owner for a ${declaration.kind}. ` +
      `Eligible owner scopes: ${describeEligibleScopes(declaration)}.`,
  );
}

/**
 * Resolve the owner a **create** must persist.
 *
 * - An explicit owner id parses via {@link parseOwnerRef}.
 * - `ownerId: null` (the old host-tier sentinel) is rejected: no kind has
 *   a NULL-eligible owner.
 * - An omitted owner is forced only when the kind declares exactly one
 *   eligible scope (`org` → the active org, `user` → the calling user);
 *   otherwise the caller must choose, and the error names the options.
 *
 * This replaces `resolveEffectiveOwnerId`, which inferred the scope from
 * the *caller's permission set* — the owning scope is a property of the
 * resource kind, not of who happens to be creating one.
 */
export function requireCreateOwner(
  input: { ownerId: string | null } | undefined,
  declaration: ResourceKindDeclaration,
  caller: OwnerScopeCaller,
): OwnerRef {
  if (input) {
    if (input.ownerId === null) {
      throw new ResourceOwnerEligibilityError(
        `A ${declaration.kind} cannot be host-tier (ownerId null). ` +
          `Eligible owner scopes: ${describeEligibleScopes(declaration)}.`,
      );
    }
    return parseOwnerRef(input.ownerId, declaration);
  }

  if (declaration.eligibleScopes.length === 1) {
    const only = declaration.eligibleScopes[0];
    if (only === 'org') return { scope: 'org', ownerId: caller.organizationId };
    if (only === 'user' && caller.userId) return { scope: 'user', ownerId: caller.userId };
  }
  throw new ResourceOwnerEligibilityError(
    `A ${declaration.kind} requires an explicit owner. ` +
      `Eligible owner scopes: ${describeEligibleScopes(declaration)}.`,
  );
}

/**
 * A stored owner bucketed by shape — the discriminated union the owner
 * data audit reasons in, instead of raw `owner_id` strings. The three
 * arms mirror the audit's SQL group key exactly: a NULL column, a
 * well-formed `<prefix>_…` id (grouped by prefix), or a malformed id
 * with no usable prefix.
 */
export type StoredOwnerGroup =
  | { readonly kind: 'null' }
  | { readonly kind: 'prefix'; readonly prefix: string }
  | { readonly kind: 'malformed' };

/** Bucket one stored `ownerId` into its {@link StoredOwnerGroup}. */
export function storedOwnerGroupOf(ownerId: string | null): StoredOwnerGroup {
  if (ownerId === null) return { kind: 'null' };
  const separator = ownerId.indexOf('_');
  if (separator <= 0) return { kind: 'malformed' };
  return { kind: 'prefix', prefix: ownerId.slice(0, separator) };
}

/**
 * Classify an owner group against a kind declaration, for the owner data
 * audit. Unlike {@link parseOwnerRef} this never throws — audit rows
 * include the retired shapes (`null`, unknown prefixes) the parse
 * boundary rejects. `scope` is the human-readable group label the audit
 * ratchet keys on.
 */
export function classifyOwnerGroup(
  group: StoredOwnerGroup,
  declaration: ResourceKindDeclaration,
): { scope: string; eligible: boolean } {
  if (group.kind === 'null') return { scope: 'null', eligible: false };
  if (group.kind === 'malformed') return { scope: 'malformed', eligible: false };
  const { prefix } = group;
  for (const scope of declaration.eligibleScopes) {
    if (isCoreScope(scope)) {
      if (CORE_SCOPE_PREFIXES[scope] === prefix) return { scope, eligible: true };
    } else if (scope.idPrefix === prefix) {
      return { scope: scope.kind, eligible: true };
    }
  }
  const coreName = (Object.entries(CORE_SCOPE_PREFIXES) as [CoreEligibleScope, string][]).find(
    ([, p]) => p === prefix,
  )?.[0];
  return { scope: coreName ?? prefix, eligible: false };
}

/** {@link classifyOwnerGroup} for a raw stored `ownerId`. */
export function classifyStoredOwner(
  ownerId: string | null,
  declaration: ResourceKindDeclaration,
): { scope: string; eligible: boolean } {
  return classifyOwnerGroup(storedOwnerGroupOf(ownerId), declaration);
}
